<template>
    <layout
        title="Logs de acesso"
        :isLoading="isLoading"
        subtitle="Histórico de logins registrados pelo ms-sec-saml (por usuário, e-mail ou login)"
        titleIcon="clock"
        :wide="true"
    >
        <div class="filters mb-4">
            <div class="filters-grid">
                <b-field label="Usuário ID">
                    <b-input
                        v-model="filters.usuarioId"
                        type="number"
                        min="1"
                        placeholder="Ex.: 16239"
                        @keyup.native.enter="search"
                    />
                </b-field>
                <b-field label="Login">
                    <b-input
                        v-model="filters.login"
                        placeholder="Ex.: usuario.teste"
                        @keyup.native.enter="search"
                    />
                </b-field>
                <b-field label="E-mail">
                    <b-input
                        v-model="filters.email"
                        type="email"
                        placeholder="Ex.: usuario@movida.com.br"
                        @keyup.native.enter="search"
                    />
                </b-field>
                <b-field label="Sistema origem">
                    <b-select v-model="filters.sistemaOrigem" placeholder="Todos" expanded>
                        <option :value="null">Todos</option>
                        <option
                            v-for="origem in sistemaOrigemOptions"
                            :key="origem"
                            :value="origem"
                        >
                            {{ origem }}
                        </option>
                    </b-select>
                </b-field>
                <div class="filters-actions">
                    <b-button
                        type="is-primary"
                        icon-left="search"
                        label="Buscar"
                        :loading="isLoading"
                        @click="search"
                    />
                    <b-button
                        class="ml-2"
                        type="is-light"
                        label="Limpar"
                        @click="clearFilters"
                    />
                </div>
            </div>
            <p v-if="filterError" class="help is-danger">{{ filterError }}</p>
        </div>

        <b-table
            class="logs-table"
            :data="logs"
            :loading="isLoading"
            :paginated="true"
            backend-pagination
            :total="meta.total"
            :per-page="meta.perPage"
            :current-page="meta.page"
            pagination-position="bottom"
            :pagination-rounded="true"
            :striped="true"
            :hoverable="true"
            aria-next-label="Próxima"
            aria-previous-label="Anterior"
            aria-page-label="Página"
            aria-current-label="Página atual"
            @page-change="onPageChange"
        >
            <b-table-column field="createdAt" label="Data/Hora" v-slot="props">
                <div class="cell-datetime">
                    <span>{{ formatDate(props.row.createdAt) }}</span>
                    <span class="has-text-grey is-size-7">{{ formatTime(props.row.createdAt) }}</span>
                </div>
            </b-table-column>
            <b-table-column field="login" label="Login" v-slot="props">
                <span :title="props.row.login">{{ props.row.login || '—' }}</span>
            </b-table-column>
            <b-table-column field="email" label="E-mail" v-slot="props">
                <span :title="props.row.email">{{ props.row.email || '—' }}</span>
            </b-table-column>
            <b-table-column field="nome" label="Nome" v-slot="props">
                <span :title="props.row.nome">{{ props.row.nome || '—' }}</span>
            </b-table-column>
            <b-table-column field="sistemaOrigem" label="Origem" v-slot="props">
                <span :title="props.row.sistemaOrigem">{{ props.row.sistemaOrigem || '—' }}</span>
            </b-table-column>
            <b-table-column field="applicationName" label="App" v-slot="props">
                {{ props.row.applicationName || '—' }}
            </b-table-column>
            <b-table-column field="resultado" label="Resultado" centered v-slot="props">
                <b-tag
                    size="is-small"
                    :type="props.row.resultado === 'SUCESSO' ? 'is-success' : 'is-danger'"
                    rounded
                >
                    {{ props.row.resultado }}
                </b-tag>
            </b-table-column>
            <b-table-column field="ip" label="IP" v-slot="props">
                {{ props.row.ip || '—' }}
            </b-table-column>
            <b-table-column field="samlRequestPresent" label="SAML" centered v-slot="props">
                <b-icon
                    :icon="props.row.samlRequestPresent ? 'check' : 'minus'"
                    :type="props.row.samlRequestPresent ? 'is-success' : 'is-grey-light'"
                    size="is-small"
                />
            </b-table-column>
        </b-table>

        <p v-if="!isLoading && searched && logs.length === 0" class="has-text-grey mt-3">
            Nenhum registro encontrado para os filtros informados.
        </p>
        <p v-if="!searched" class="has-text-grey mt-3">
            Informe usuário ID, login ou e-mail e clique em Buscar.
        </p>
    </layout>
</template>

<script>
import { Component, Vue } from 'vue-property-decorator'
import services from '@/http'

@Component
export default class Index extends Vue {
    data() {
        return {
            isLoading: false,
            searched: false,
            filterError: null,
            logs: [],
            filters: {
                usuarioId: null,
                login: '',
                email: '',
                sistemaOrigem: null,
            },
            meta: {
                page: 1,
                perPage: 20,
                total: 0,
                lastPage: 1,
            },
            sistemaOrigemOptions: [
                'JIRA',
                'SPLITC',
                'NEXTQS',
                'NEXTQS-AGENT',
                'FRESHSERVICE',
                'SAML-DESCONHECIDO',
                'VETOR-DIRETO',
            ],
        }
    }

    buildParams(page = 1) {
        const params = {
            page,
            perPage: this.meta.perPage,
        }

        if (this.filters.usuarioId) {
            params.usuarioId = Number(this.filters.usuarioId)
        }
        if (this.filters.login && String(this.filters.login).trim() !== '') {
            params.login = String(this.filters.login).trim()
        }
        if (this.filters.email && String(this.filters.email).trim() !== '') {
            params.email = String(this.filters.email).trim()
        }
        if (this.filters.sistemaOrigem) {
            params.sistemaOrigem = this.filters.sistemaOrigem
        }

        return params
    }

    hasRequiredFilter() {
        return Boolean(
            this.filters.usuarioId
            || (this.filters.login && String(this.filters.login).trim() !== '')
            || (this.filters.email && String(this.filters.email).trim() !== '')
        )
    }

    search() {
        this.filterError = null

        if (!this.hasRequiredFilter()) {
            this.filterError = 'Informe ao menos um filtro: Usuário ID, Login ou E-mail.'
            return
        }

        this.list(1)
    }

    clearFilters() {
        this.filters = {
            usuarioId: null,
            login: '',
            email: '',
            sistemaOrigem: null,
        }
        this.logs = []
        this.searched = false
        this.filterError = null
        this.meta = {
            page: 1,
            perPage: 20,
            total: 0,
            lastPage: 1,
        }
    }

    onPageChange(page) {
        if (!this.hasRequiredFilter()) {
            return
        }
        this.list(page)
    }

    list(page = 1) {
        this.isLoading = true
        this.searched = true

        services.session.list(this.buildParams(page))
            .then((res) => {
                const payload = res.body || res.data || {}
                const data = payload.data || {}
                this.logs = data.items || []
                this.meta = {
                    page: (data.meta && data.meta.page) || page,
                    perPage: (data.meta && data.meta.perPage) || this.meta.perPage,
                    total: (data.meta && data.meta.total) || 0,
                    lastPage: (data.meta && data.meta.lastPage) || 1,
                }
            })
            .catch((err) => {
                this.logs = []
                this.$root.flashError(err)
            })
            .finally(() => {
                this.isLoading = false
            })
    }

    formatDate(value) {
        if (!value) {
            return '—'
        }

        return String(value).split(' ')[0] || '—'
    }

    formatTime(value) {
        if (!value) {
            return ''
        }

        return String(value).split(' ')[1] || ''
    }
}
</script>

<style scoped>
.filters-grid {
    display: grid;
    grid-template-columns: minmax(120px, 1fr) minmax(160px, 1.4fr) minmax(200px, 1.8fr) minmax(160px, 1.2fr) auto;
    gap: 1rem;
    align-items: end;
}

.filters-actions {
    justify-self: end;
    align-self: end;
    display: flex;
    align-items: center;
    padding-bottom: 0.375rem;
}

.logs-table >>> .table {
    font-size: 0.875rem;
    width: 100%;
}

.logs-table >>> th,
.logs-table >>> td {
    vertical-align: middle;
    padding: 0.65rem 0.75rem;
}

.logs-table >>> th {
    white-space: nowrap;
    font-size: 0.8rem;
}

.cell-datetime {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
    white-space: nowrap;
}

@media screen and (max-width: 1024px) {
    .filters-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .filters-actions {
        grid-column: 1 / -1;
        justify-self: start;
    }
}
</style>
